/*
 * Purpose: Map AI assistant — GOAL-centric context, suggest edits, apply on approval
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { planFromOutcomeAsync } from '@/services/autoblocks/planFromOutcomeAsync';
import { rehydratePlanConnectors } from '@/services/autoblocks/planReadiness';
import { buildRuleBasedFlowSummary } from '@/services/autoblocks/flowSummary';
import { isMinimalMissionFlow } from '@/lib/applyPlanToMap';
import { buildGoalCentricMapContext } from '@/lib/mapAssistantContext';
import { getMissionGoalIntent } from '@/lib/mapNodeSummary';
import type { Workflow } from '@/stores/workflowStore';
import type { OutcomePlan } from '@/services/autoblocks/types';

export type MapChatTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export type MapChatApplyMode = 'auto' | 'confirm';

export type MapChatSuggestedAction = {
  id: string;
  label: string;
  detail: string;
  /** block nodeId when editing in place */
  nodeId?: string;
};

export type MapChatPlanResult = {
  plan: OutcomePlan;
  /** True when map had content and user is refining (not replacing) */
  isRefinement: boolean;
  applyMode: MapChatApplyMode;
  goalIntent: string;
  suggestedActions: MapChatSuggestedAction[];
};

function hasOperationalFlow(workflow: Workflow | null | undefined): boolean {
  if (!workflow) return false;
  return workflow.nodes.some(
    (n) =>
      n.type === 'trigger' ||
      n.type === 'tool' ||
      n.type === 'action' ||
      n.type === 'agent' ||
      n.type === 'drone'
  );
}

function isFullReplaceRequest(message: string): boolean {
  return /\b(start over|from scratch|replace (the )?flow|clear (the )?map|reset (the )?map|new flow entirely|ignore (the )?current|brand new|completely new)\b/i.test(
    message
  );
}

const REFINEMENT_CUES =
  /\b(add|also|change|update|remove|delete|instead|before|after|move|connect|disconnect|replace|rename|tweak|adjust|fix|swap|insert|drop|extra|modify|reorder|wire|hook up|another step|same flow|current map|this map|these blocks|already on|block intent|task outcome|mcp goal)\b/i;

const NEW_OUTCOME_CUES =
  /\b(when|every|whenever|each time|if .+ then|automate|build (a|me )?(new )?flow|different mission|new mission)\b/i;

const AUTO_APPLY_CUES =
  /\b(apply|wire|update the map|put (it )?on the map|go ahead|yes[,]? do|do it now|build it|make it so|ship it)\b/i;

function isRefinementRequest(message: string, workflow: Workflow | null | undefined): boolean {
  if (!workflow || isMinimalMissionFlow(workflow)) return false;
  if (!hasOperationalFlow(workflow)) return false;
  if (isFullReplaceRequest(message)) return false;
  if (REFINEMENT_CUES.test(message)) return true;
  if (/\b(this|that|it|the map|current flow|above|existing blocks?)\b/i.test(message)) return true;
  if (NEW_OUTCOME_CUES.test(message) && !REFINEMENT_CUES.test(message)) return false;
  return false;
}

/** Auto-apply when empty scaffold or user explicitly asks; else confirm before map edit. */
export function shouldAutoApplyMapPlan(
  message: string,
  workflow: Workflow | null | undefined,
  isRefinement: boolean
): boolean {
  if (AUTO_APPLY_CUES.test(message)) return true;
  if (isFullReplaceRequest(message)) return true;
  if (isMinimalMissionFlow(workflow)) return true;
  if (isRefinement) return false;
  if (hasOperationalFlow(workflow)) return false;
  return true;
}

function buildSuggestedActions(plan: OutcomePlan): MapChatSuggestedAction[] {
  return plan.steps.map((step, index) => ({
    id: step.id || `step-${index}`,
    label: step.label,
    detail: [step.blockIntent, step.taskOutcome].filter(Boolean).join(' → ') || step.rationale,
    nodeId: step.config?.refineNodeId ? String(step.config.refineNodeId) : step.id,
  }));
}

export async function planFlowFromMapChat(
  history: MapChatTurn[],
  latestUserMessage: string,
  currentWorkflow?: Workflow | null
): Promise<MapChatPlanResult> {
  const trimmed = latestUserMessage.trim();
  if (!trimmed) {
    throw new Error('Describe what you want this flow to accomplish.');
  }

  const goalIntent = getMissionGoalIntent(currentWorkflow);
  const mapContext = buildGoalCentricMapContext(currentWorkflow);
  const refinement = isRefinementRequest(trimmed, currentWorkflow);
  const applyMode: MapChatApplyMode = shouldAutoApplyMapPlan(trimmed, currentWorkflow, refinement)
    ? 'auto'
    : 'confirm';

  const transcript = history
    .slice(-8)
    .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
    .join('\n');

  const instruction = refinement
    ? 'Refine FULFILLMENT BLOCKS to satisfy the user message while honoring the GOAL. Reuse nodeId as step.id for blocks you edit in place; update blockIntent, taskOutcome, and config fields. Return a complete updated plan.'
    : isMinimalMissionFlow(currentWorkflow)
      ? 'Design fulfillment blocks wired START → GOAL from the MISSION GOAL. Each block needs blockIntent + taskOutcome.'
      : hasOperationalFlow(currentWorkflow)
        ? 'Extend or revise fulfillment blocks per the user message. Preserve blocks that still serve the GOAL unless user asked to remove them.'
        : 'Design fulfillment blocks on the map from the MISSION GOAL.';

  const prompt = [
    `MISSION GOAL (SSOT): ${goalIntent}`,
    transcript ? `CHAT HISTORY:\n${transcript}` : '',
    `User request: ${trimmed}`,
    '',
    instruction,
  ]
    .filter(Boolean)
    .join('\n');

  const plan = await planFromOutcomeAsync(prompt, { mapContext });
  return {
    plan: rehydratePlanConnectors(plan),
    isRefinement: refinement,
    applyMode,
    goalIntent,
    suggestedActions: buildSuggestedActions(plan),
  };
}

export function summarizePlanForChat(
  plan: OutcomePlan,
  options?: {
    isRefinement?: boolean;
    priorWorkflow?: Workflow | null;
    applyMode?: MapChatApplyMode;
    suggestedActions?: MapChatSuggestedAction[];
  }
): string {
  const actions = options?.suggestedActions ?? buildSuggestedActions(plan);
  const stepLines =
    actions.length > 0
      ? actions.slice(0, 8).map((a) => `• **${a.label}**${a.detail ? ` — ${a.detail}` : ''}`).join('\n')
      : plan.steps.slice(0, 8).map((s) => `• ${s.label}`).join('\n');

  const connectorLines =
    (plan.resolvedConnectors ?? plan.connectors).slice(0, 4).map((c) => `• ${c.name}`).join('\n') ||
    '• Built-in blocks only';

  const gaps =
    plan.gaps.length > 0 ? `\n\nNotes:\n${plan.gaps.slice(0, 2).map((g) => `• ${g}`).join('\n')}` : '';

  const priorSummary =
    options?.priorWorkflow && hasOperationalFlow(options.priorWorkflow)
      ? buildRuleBasedFlowSummary(options.priorWorkflow)
      : null;

  const changeLine = options?.isRefinement && priorSummary
    ? `\n\n_Editing fulfillment blocks for GOAL_ (was: ${priorSummary.intent.slice(0, 80)}${priorSummary.intent.length > 80 ? '…' : ''})`
    : options?.isRefinement
      ? '\n\n_Editing blocks that fulfill your GOAL._'
      : '\n\n_Wiring blocks from your GOAL._';

  const applyLine =
    options?.applyMode === 'confirm'
      ? '\n\n**Review the plan** — tap **Apply to map** below to update blocks and content on the canvas.'
      : '\n\n**Map updated** — review on the canvas, then **Check** before **Start**.';

  return [
    `**${plan.interpretedGoal}**`,
    changeLine,
    '',
    `${actions.length || plan.steps.length} suggested step(s):`,
    stepLines,
    (actions.length || plan.steps.length) > 8 ? '• …' : '',
    '',
    'Connectors:',
    connectorLines,
    gaps,
    applyLine,
  ]
    .filter(Boolean)
    .join('\n');
}
